import '../../i18n/autoTranslate';
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import SpotlightCard from '../../../components/ui/SpotlightCard';

const DetailedFeedbackPanel = ({ 
  feedbackSections = [
    {
      id: 'communication',
      title: 'Communication Analysis',
      icon: 'MessageSquare',
      score: 85,
      insights: [
        {
          type: 'positive',
          title: 'Clear Articulation',
          description: 'You spoke clearly and at an appropriate pace throughout the interview.',
          details: 'Average speaking pace: 145 words per minute (optimal range: 140-160 wpm)'
        },
        {
          type: 'improvement',
          title: 'Filler Words',
          description: 'Reduce usage of filler words like "um", "uh", and "like".',
          details: 'Detected 12 filler words in 45 minutes (target: <8 for professional interviews)'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Assessment',
      icon: 'Code',
      score: 78,
      insights: [
        {
          type: 'positive',
          title: 'Problem-Solving Approach',
          description: 'Demonstrated systematic thinking and logical problem breakdown.',
          details: 'Successfully identified key components and explained your reasoning clearly.'
        },
        {
          type: 'improvement',
          title: 'Code Optimization',
          description: 'Consider discussing time and space complexity more thoroughly.',
          details: 'Mentioned Big O notation but could elaborate on optimization strategies.'
        }
      ]
    },
    {
      id: 'behavioral',
      title: 'Behavioral Responses',
      icon: 'Users',
      score: 92,
      insights: [
        {
          type: 'positive',
          title: 'STAR Method Usage',
          description: 'Excellent use of Situation, Task, Action, Result framework.',
          details: '4 out of 5 behavioral questions followed STAR structure effectively.'
        },
        {
          type: 'positive',
          title: 'Specific Examples',
          description: 'Provided concrete, relevant examples from your experience.',
          details: 'Examples were well-detailed and demonstrated clear impact and learning.'
        }
      ]
    },
    {
      id: 'nonverbal',
      title: 'Non-Verbal Assessment',
      icon: 'Eye',
      score: 88,
      insights: [
        {
          type: 'positive',
          title: 'Eye Contact',
          description: 'Maintained good eye contact throughout the session.',
          details: 'Eye contact maintained 78% of the time (excellent for video interviews).'
        },
        {
          type: 'improvement',
          title: 'Posture',
          description: 'Slight slouching detected in the latter half of the interview.',
          details: 'Consider maintaining upright posture to project confidence and engagement.'
        }
      ]
    }
  ],
  isLoading = false
}) => {
  const [expandedSections, setExpandedSections] = useState(new Set(['communication']));

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded?.has(sectionId)) {
      newExpanded?.delete(sectionId);
    } else {
      newExpanded?.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-indigo-400';
    return 'text-yellow-400';
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'positive':
        return { name: 'CheckCircle', color: 'text-green-400' };
      case 'improvement':
        return { name: 'AlertCircle', color: 'text-yellow-400' };
      case 'critical':
        return { name: 'XCircle', color: 'text-red-400' };
      default:
        return { name: 'Info', color: 'text-gray-400' };
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3]?.map(i => (
          <SpotlightCard key={i} spotlightColor="rgba(139, 92, 246, 0.15)">
            <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  p-6 animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-gray-700/50 rounded"></div>
                  <div className="h-5 bg-gray-700/50 rounded w-32"></div>
                </div>
                <div className="h-6 bg-gray-700/50 rounded w-12"></div>
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-700/50 rounded w-full"></div>
                <div className="h-4 bg-gray-700/50 rounded w-3/4"></div>
              </div>
            </div>
          </SpotlightCard>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white">Detailed Feedback</h3>
          <p className="text-sm text-gray-400 mt-1">
            AI-powered analysis with specific observations and recommendations
          </p>
        </div>
        <button
          onClick={() => setExpandedSections(expandedSections?.size === feedbackSections?.length ? new Set() : new Set(feedbackSections.map(s => s.id)))}
          className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white transition-all duration-200 border border-gray-700"
        >
          <Icon name={expandedSections?.size === feedbackSections?.length ? "ChevronUp" : "ChevronDown"} size={16} />
          <span className="text-sm font-medium">
            {expandedSections?.size === feedbackSections?.length ? 'Collapse All' : 'Expand All'}
          </span>
        </button>
      </div>

      {feedbackSections?.map((section) => {
        const isExpanded = expandedSections?.has(section?.id);
        
        return (
          <SpotlightCard key={section?.id} spotlightColor="rgba(139, 92, 246, 0.15)">
            <div className="bg-gradient-to-br from-[rgba(30,30,46,0.9)] via-[rgba(30,30,46,0.8)] to-[rgba(46,46,69,0.5)] border border-white/10  overflow-hidden">
              <button
                onClick={() => toggleSection(section?.id)}
                className="w-full p-6 text-left hover:bg-white/5 transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-500/20">
                      <Icon name={section?.icon} size={20} className="text-indigo-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white">{section?.title}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-sm text-gray-400">Score:</span>
                        <span className={`text-sm font-bold ${getScoreColor(section?.score)}`}>
                          {section?.score}/100
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <div className="text-sm text-gray-400">
                        {section?.insights?.length} insights
                      </div>
                    </div>
                    <Icon 
                      name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                      size={20} 
                      className="text-gray-400 transition-transform duration-200"
                    />
                  </div>
                </div>
              </button>

              {isExpanded && (
                <div className="px-6 pb-6 animate-fade-in">
                  <div className="border-t border-gray-700 pt-6">
                    <div className="space-y-4">
                      {section?.insights?.map((insight, index) => {
                        const iconInfo = getInsightIcon(insight?.type);
                        
                        return (
                          <div key={index} className="flex space-x-4 p-4 rounded-lg bg-gray-800/20 border border-gray-700/50">
                            <div className="flex-shrink-0 mt-1">
                              <Icon name={iconInfo?.name} size={20} className={iconInfo?.color} />
                            </div>
                            <div className="flex-1 space-y-2">
                              <div>
                                <h5 className="font-semibold text-white">{insight?.title}</h5>
                                <p className="text-sm text-gray-300 mt-1">{insight?.description}</p>
                              </div>
                              {insight?.details && (
                                <div className="p-3 bg-gray-800/40 rounded-lg border border-gray-700/30">
                                  <p className="text-xs text-gray-400" dangerouslySetInnerHTML={{ __html: insight?.details }} />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </SpotlightCard>
        );
      })}
    </div>
  );
};

export default DetailedFeedbackPanel;
